import { StateCreator } from 'zustand/index';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { createStore, StoreMutatorIdentifier } from 'zustand/vanilla';
import { EqualityFn, SetInitialState, StoreHook } from '../../types';

/**
 * This is a reimplementation of Zustand function but with added ability to manipulate initialState for hydration and server rendering.
 */
export const createWithEqualityFn = <
  State,
  Mos extends [StoreMutatorIdentifier, unknown][] = []
>(
  createState: StateCreator<State, [], Mos>,
  defaultEqualityFn?: EqualityFn
): StoreHook<State> => {
  const api = createStore(createState);
  let initialState = api.getInitialState();

  api.getInitialState = () => {
    return initialState;
  };

  const setInitialState: SetInitialState<State> = (value) => {
    const nextState =
      typeof value === 'function'
        ? (value as (state: State) => State)(initialState)
        : value;

    initialState = { ...initialState, ...nextState };
    api.setState(initialState, true);
  };

  const useBoundStoreWithEqualityFn = ((
    selector,
    equalityFn = defaultEqualityFn
  ) => useStoreWithEqualityFn(api, selector, equalityFn)) as StoreHook<State>;

  Object.assign(useBoundStoreWithEqualityFn, api);
  useBoundStoreWithEqualityFn.setInitialState = setInitialState;

  return useBoundStoreWithEqualityFn;
};
